import { useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  addDays,
  addMonths,
  clampDate,
  dayLabel,
  earlierOf,
  EARLIEST_DATE,
  formatDate,
  isOffDay,
  LATEST_DATE,
  laterOf,
  monthGrid,
  monthTitle,
  outOfRange,
  parseDateInput,
  partsOf,
  weekdayIndex,
  WEEKDAYS_SHORT,
} from '../../lib/date-field.ts';
import { useScreen } from '../../lib/layout.ts';
import { cn } from '../../lib/utils.ts';
import { Popover } from './popover.tsx';
import { fieldClass } from './variants.ts';

const FORMAT_ERROR = 'Bitte ein Datum als TT.MM.JJJJ eingeben.';

/** Fehlersatz unter dem Feld nach dem Verlassen (Format, zu früh, zu spät). */
function errorText(text: string, min?: string, max?: string): string | null {
  const parsed = parseDateInput(text);
  if ('empty' in parsed) return null;
  if ('error' in parsed) return FORMAT_ERROR;
  const range = outOfRange(parsed.iso, min, max);
  if (range === 'min' && min) return `Frühestens ${formatDate(min)}.`;
  if (range === 'max' && max) return `Spätestens ${formatDate(max)}.`;
  return null;
}

/**
 * Datumsfeld mit Kalender (DESIGN U23 „Datumsfeld mit Kalender“, Ticket 1.215) – gilt für alle Datumsfelder im ERP und
 * ersetzt `<Input type="date">`. Wert wie bisher ISO `JJJJ-MM-TT` (Kalendertag Europe/Berlin), Anzeige `TT.MM.JJJJ`.
 * Tippen bleibt möglich („26.2.27“, „1.3.2027“, eingefügtes ISO); ungültig → nach Verlassen roter Rand und Satz unter dem
 * Feld. Klick ins Feld öffnet den Monatskalender, der Fokus bleibt im Feld; Alt+↓ oder das Kalender-Symbol öffnet mit
 * Fokus im Raster (Pfeile ±1/±7 Tage, Bild↑/↓ Monat, Pos1/Ende Wochenanfang/-ende, Enter/Leertaste wählt, Esc schließt).
 * Am Handy bleibt Tippen ins Feld Tippen; das Symbol öffnet ein Blatt von unten. Keine Uhr im Baustein: `today` (ISO)
 * kommt von der Seite (`businessDate(useBusinessNow().now)`).
 */
export function DateField({
  id,
  value,
  onChange,
  today,
  min,
  max,
  invalid,
  disabled,
  required,
  name,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  className,
  onInvalidChange,
}: {
  id?: string;
  /** ISO `JJJJ-MM-TT` oder leer. */
  value: string;
  /** Neuer Wert (ISO) bei gültiger Eingabe bzw. Wahl im Kalender; `''` beim Leeren. */
  onChange: (value: string) => void;
  /** Heutiger Kalendertag (ISO), z. B. aus `useBusinessNow`; Ring im Kalender und Start ohne Wert. */
  today: string;
  min?: string;
  max?: string;
  invalid?: boolean;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
  /**
   * Meldet der Seite, ob der getippte Text gerade unbrauchbar ist: Fehlersatz („Bitte ein Datum als TT.MM.JJJJ
   * eingeben.“, „Frühestens …“, „Spätestens …“) oder `null`. Kommt sofort beim Tippen (nicht erst beim Verlassen), damit
   * die Seite Speichern sperren kann; `value` bleibt dabei der letzte gültige Wert.
   */
  onInvalidChange?: (message: string | null) => void;
}) {
  const ownId = useId();
  const inputId = id ?? `${ownId}-feld`;
  const errorId = `${ownId}-fehler`;
  const titleId = `${ownId}-monat`;
  const phone = useScreen() === 'phone';
  const input = useRef<HTMLInputElement>(null);
  const calendar = useRef<HTMLDivElement>(null);

  const [text, setText] = useState(() => formatDate(value));
  const [error, setError] = useState<string | null>(null);
  // Wert von außen geändert (nicht durch eigenes Tippen): Anzeige nachziehen (abgeleiteter Zustand ohne Effekt).
  const [shownValue, setShownValue] = useState(value);
  if (value !== shownValue) {
    setShownValue(value);
    const typed = parseDateInput(text);
    if (!('iso' in typed && typed.iso === value) && !('empty' in typed && value === '')) {
      setText(formatDate(value));
      setError(null);
    }
  }

  // Kalender blättert nur im unterstützten Bereich, auch ohne min/max.
  const lo = laterOf(min, EARLIEST_DATE);
  const hi = earlierOf(max, LATEST_DATE);

  // Gültigkeit des getippten Texts an die Seite melden (nur bei Änderung).
  const typedError = errorText(text, min, max);
  const reportInvalid = useRef(onInvalidChange);
  useEffect(() => {
    reportInvalid.current = onInvalidChange;
  });
  useEffect(() => {
    reportInvalid.current?.(typedError);
  }, [typedError]);

  const start = (): string => clampDate(partsOf(value) ? value : today, lo, hi);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(() => start());
  // Fokus ins Raster (Alt+↓, Symbol, Pfeiltasten) – nicht bei Klick ins Feld.
  const gridFocus = useRef(false);
  // Erneuter Fokuswunsch bei schon offenem Kalender (Klick ins Feld, dann Alt+↓) – sonst liefe der Effekt nicht neu.
  const [focusRequest, setFocusRequest] = useState(0);

  function openCalendar(focusGrid: boolean) {
    if (disabled) return;
    gridFocus.current = focusGrid;
    if (focusGrid) setFocusRequest((n) => n + 1);
    if (!open) setActive(start());
    setOpen(true);
  }

  function close(focusField: boolean) {
    setOpen(false);
    gridFocus.current = false;
    if (focusField && !phone) input.current?.focus();
  }

  function choose(iso: string) {
    setText(formatDate(iso));
    setError(null);
    onChange(iso);
    close(true);
  }

  useEffect(() => {
    if (!open || !gridFocus.current) return;
    // Nach dem Blatt (Handy), das beim Öffnen den Fokus auf sein erstes Element setzt.
    const frame = requestAnimationFrame(() => {
      calendar.current?.querySelector<HTMLElement>(`[data-iso="${active}"]`)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open, active, focusRequest]);

  function onType(next: string) {
    setText(next);
    const parsed = parseDateInput(next);
    if ('empty' in parsed) {
      setError(null);
      if (value !== '') onChange('');
      return;
    }
    if ('iso' in parsed && !outOfRange(parsed.iso, min, max)) {
      // Ein gezeigter Fehler verschwindet, sobald die Eingabe stimmt; neue Fehler erst nach dem Verlassen.
      setError(null);
      if (open) setActive(parsed.iso);
      if (parsed.iso !== value) onChange(parsed.iso);
    }
  }

  function onFieldBlur() {
    const parsed = parseDateInput(text);
    const message = errorText(text, min, max);
    setError(message);
    if ('iso' in parsed && !message) setText(formatDate(parsed.iso));
  }

  function onFieldKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.altKey && e.key === 'ArrowDown') {
      e.preventDefault();
      openCalendar(true);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      close(false);
    }
  }

  // PC/Tablet: verlässt der Fokus Feld und Kalender (Tab), schließt der Kalender. Am Handy liegt das Blatt im Portal.
  function onGroupBlur(e: FocusEvent<HTMLDivElement>) {
    if (phone || !open) return;
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    if (!next) return;
    setOpen(false);
  }

  function onGridKey(e: KeyboardEvent<HTMLButtonElement>, iso: string) {
    let next: string;
    switch (e.key) {
      case 'ArrowLeft':
        next = addDays(iso, -1);
        break;
      case 'ArrowRight':
        next = addDays(iso, 1);
        break;
      case 'ArrowUp':
        next = addDays(iso, -7);
        break;
      case 'ArrowDown':
        next = addDays(iso, 7);
        break;
      case 'PageUp':
        next = addMonths(iso, -1);
        break;
      case 'PageDown':
        next = addMonths(iso, 1);
        break;
      case 'Home':
        next = addDays(iso, -weekdayIndex(iso));
        break;
      case 'End':
        next = addDays(iso, 6 - weekdayIndex(iso));
        break;
      default:
        return;
    }
    e.preventDefault();
    gridFocus.current = true;
    setActive(clampDate(next, lo, hi));
  }

  const describedBy = [ariaDescribedBy, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  const view = partsOf(active) ?? partsOf(today) ?? { year: 2000, month: 1, day: 1 };
  const weeks = monthGrid(view.year, view.month);
  const shiftMonth = (by: number) => {
    gridFocus.current = false;
    setActive(clampDate(addMonths(active, by), lo, hi));
  };
  const firstOfView = `${String(view.year).padStart(4, '0')}-${String(view.month).padStart(2, '0')}-01`;
  const prevDisabled = addDays(firstOfView, -1) < lo;
  const nextDisabled = addMonths(firstOfView, 1) > hi;

  return (
    <div data-date-field="" className={cn('w-field-m max-w-full', className)} onBlur={onGroupBlur}>
      <Popover
        open={open}
        onOpenChange={(next) => (next ? openCalendar(true) : close(false))}
        label="Kalender"
        fixed
        autoFocus={false}
        trigger={(props) => (
          <>
            <input
              ref={input}
              id={inputId}
              name={name}
              type="text"
              autoComplete="off"
              spellCheck={false}
              placeholder="TT.MM.JJJJ"
              value={text}
              disabled={disabled}
              required={required}
              aria-label={ariaLabel}
              aria-invalid={invalid || !!error || undefined}
              aria-describedby={describedBy}
              aria-haspopup="dialog"
              // Offen/zu meldet der Kalender-Knopf; an einem Textfeld ist aria-expanded nicht erlaubt (axe).
              aria-controls={props['aria-controls']}
              onChange={(e) => onType(e.target.value)}
              onFocus={() => {
                gridFocus.current = false;
              }}
              onBlur={onFieldBlur}
              onKeyDown={onFieldKey}
              onClick={() => {
                // Am Handy ist Tippen ins Feld Tippen; der Kalender kommt über das Symbol.
                if (!phone) openCalendar(false);
              }}
              data-field-box={disabled ? 'locked' : 'edit'}
              className={cn(fieldClass({ invalid: invalid || !!error, locked: !!disabled }), 'h-11 w-full pr-[var(--field-end-date)] pl-space-4')}
            />
            <button
              type="button"
              aria-label="Kalender öffnen"
              aria-expanded={props['aria-expanded']}
              aria-controls={props['aria-controls']}
              aria-haspopup="dialog"
              disabled={disabled}
              onClick={() => (open ? close(true) : openCalendar(true))}
              className="absolute top-1/2 right-1 grid size-9 -translate-y-1/2 cursor-pointer place-items-center rounded-full text-ink-2 hover:bg-tile hover:text-ink disabled:cursor-default disabled:opacity-60 pointer-coarse:right-0 pointer-coarse:size-11"
            >
              <CalendarDays size={18} strokeWidth={1.5} aria-hidden />
            </button>
          </>
        )}
        className="p-space-3"
      >
        <div
          ref={calendar}
          data-testid="kalender"
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            close(true);
          }}
        >
          <div className="mb-space-2 flex items-center justify-between gap-space-2">
            <button
              type="button"
              aria-label="Voriger Monat"
              disabled={prevDisabled}
              onClick={() => shiftMonth(-1)}
              className="grid size-9 cursor-pointer place-items-center rounded-full text-ink-2 hover:bg-tile hover:text-ink disabled:cursor-default disabled:opacity-40 pointer-coarse:size-11"
            >
              <ChevronLeft size={18} strokeWidth={1.5} aria-hidden />
            </button>
            <span id={titleId} aria-live="polite" className="text-row font-semibold text-ink">
              {monthTitle(view.year, view.month)}
            </span>
            <button
              type="button"
              aria-label="Nächster Monat"
              disabled={nextDisabled}
              onClick={() => shiftMonth(1)}
              className="grid size-9 cursor-pointer place-items-center rounded-full text-ink-2 hover:bg-tile hover:text-ink disabled:cursor-default disabled:opacity-40 pointer-coarse:size-11"
            >
              <ChevronRight size={18} strokeWidth={1.5} aria-hidden />
            </button>
          </div>
          <table role="grid" aria-labelledby={titleId} data-no-export="Kalender zum Wählen eines Tages, keine Datenliste" className="mx-auto border-collapse">
            <thead>
              <tr>
                {WEEKDAYS_SHORT.map((d, i) => (
                  <th key={d} scope="col" abbr={['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'][i]} className="type-meta h-8 font-normal text-ink-3">
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week) => (
                <tr key={week[0]!.iso}>
                  {week.map((day) => {
                    if (!day.inMonth) return <td key={day.iso} role="gridcell" className="size-9 p-0 pointer-coarse:size-11" />;
                    const selected = day.iso === value;
                    const isToday = day.iso === today;
                    const blocked = outOfRange(day.iso, lo, hi) !== null;
                    return (
                      <td key={day.iso} role="gridcell" aria-selected={selected} className="p-0 text-center">
                        <button
                          type="button"
                          data-iso={day.iso}
                          tabIndex={day.iso === active ? 0 : -1}
                          aria-label={dayLabel(day.iso)}
                          aria-current={isToday ? 'date' : undefined}
                          disabled={blocked}
                          onClick={() => choose(day.iso)}
                          onKeyDown={(e) => onGridKey(e, day.iso)}
                          onFocus={() => setActive(day.iso)}
                          className={cn(
                            'grid size-9 cursor-pointer place-items-center rounded-full text-row tabular-nums focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring disabled:cursor-default disabled:opacity-35 pointer-coarse:size-11',
                            selected
                              ? 'bg-brand font-semibold text-on-brand'
                              : cn('hover:bg-tile', isOffDay(day.iso) ? 'text-ink-3' : 'text-ink', isToday && 'border border-brand'),
                          )}
                        >
                          {Number(day.iso.slice(8, 10))}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Popover>
      {error ? (
        <p id={errorId} className="type-meta mt-space-2 text-clarify">
          {error}
        </p>
      ) : null}
    </div>
  );
}
